import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { NeoCardModule } from '@neogov/pattern-library/neo-card';
import { ActionButtonComponent } from '../action-button/action-button.component';
import { IconComponent } from '../icon';

@Component({
  selector: 'cm-alert-card',
  standalone: true,
  imports: [NeoCardModule, ActionButtonComponent, IconComponent],
  templateUrl: './alert-card.component.html',
  styleUrl: './alert-card.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertCardComponent {
  private static _nextId = 0;
  public readonly contentId = `alert-card-content-${AlertCardComponent._nextId++}`;
  public readonly headingId = `alert-card-heading-${AlertCardComponent._nextId}`;

  public readonly color = input<'primary' | 'warning' | 'info'>('primary');
  public readonly icon = input<string>('error');
  public readonly title = input<string>('');
  public readonly subtitle = input<string>('');
  public readonly collapsible = input<boolean>(false);
  public readonly initiallyExpanded = input<boolean>(false);
  public readonly titleSize = input<'lg' | 'md' | 's' | 'xs'>('lg');
  public readonly iconSize = input<'lg' | 'md' | 's' | 'xs'>('lg');

  /** Shows a close button that removes the alert. @default false */
  public readonly dismissible = input<boolean>(false);

  /** Emitted when the user closes the alert. */
  public readonly dismissed = output<void>();

  private readonly _manuallyToggled = signal<boolean | null>(null);
  private readonly _isDismissed = signal(false);

  public readonly expanded = computed(() => this._manuallyToggled() ?? this.initiallyExpanded());
  public readonly isDismissed = this._isDismissed.asReadonly();

  public readonly cardClasses = computed(
    () => `${this.color()} title-${this.titleSize()}${this.dismissible() ? ' is-dismissible' : ''}`,
  );

  /** Distinguishes the control when several alerts share a screen. */
  public readonly dismissLabel = computed(() =>
    this.title() ? `Dismiss ${this.title()}` : 'Dismiss alert',
  );

  public toggleExpanded(): void {
    this._manuallyToggled.set(!this.expanded());
  }

  /**
   * Removes the alert. The event is swallowed so closing a collapsible alert does
   * not also toggle the header it sits in.
   */
  public onDismiss(event: Event): void {
    event.stopPropagation();
    this._isDismissed.set(true);
    this.dismissed.emit();
  }
}
